import React, { ReactNode, useEffect } from 'react';
import { App, Col, Divider, Row, Skeleton, Tooltip, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

import { selectShopCurrent } from '../../../../shops/features/shops/store/shopSlice';
import getEnvConfig from '../../../../../utils/config/env';
import { getContentBlocks } from '../utils/getMassBroadcastsModalContent';
import { GenericModal } from '../../../../../shared/ui/GenericModal';
import { MassBroadcastsModalButtons } from '../components/MassBroadcastsModalButtons';
import { broadcastStatus } from '../utils/options';
import { useAppSelector } from '../../../../../store';
import {
    useLazyReadBroadcastQuery,
    useUpdateBroadcastMutation,
} from '../service/broadcasts.api';

const { Text } = Typography;

interface Props {
    readonly name: string;
    readonly component: ReactNode;
    readonly icon?: ReactNode;
    readonly tooltip?: string;
}
const renderContent = ({ name, component, icon, tooltip }: Props) => {
    return (
        <Row gutter={[48, 0]}>
            <Col span={12}>
                {tooltip ? (
                    <Tooltip title={tooltip}>
                        <Text>{name}</Text> {icon}
                    </Tooltip>
                ) : (
                    <>
                        <Text>{name}</Text> {icon}
                    </>
                )}
            </Col>
            <Col span={12}>{component}</Col>
            <Divider style={{ margin: '16px 0' }} />
        </Row>
    );
};

export const MassBroadcastsModal = () => {
    const { broadcastId } = useParams();
    const navigate = useNavigate();
    const { notification } = App.useApp();

    const current = useAppSelector(selectShopCurrent);

    const [getBroadcast, { data, isLoading }] = useLazyReadBroadcastQuery();

    const [
        updateBroadcast,
        { isSuccess, isLoading: isLoadingUpdate, isError, error },
    ] = useUpdateBroadcastMutation();

    useEffect(() => {
        if (isSuccess) {
            notification.success({
                message: 'Рассылка успешно отменена',
            });
            navigate(-1);
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError) {
            notification.error({
                message: 'Не получилось отменить',
            });
        }
    }, [isError]);

    useEffect(() => {
        if (broadcastId && current.id) {
            getBroadcast({ id: current.id, broadcast_id: +broadcastId }, true);
        }
    }, [broadcastId, current]);

    const handleCloseModal = () => {
        navigate(-1);
    };

    const handleEdit = () => {
        navigate(
            `${getEnvConfig().PUBLIC_PATH}/crm/mass-broadcasts/${
                current.id
            }/edit/${broadcastId}`
        );
    };

    const handleCansel = () => {
        updateBroadcast({
            shop_id: current.id,
            broadcast_id: +broadcastId,
            body: {
                status: broadcastStatus.cancelled,
            },
        });
    };

    const contentBlocks = getContentBlocks(data);

    return (
        <GenericModal
            title={data?.title || `Рассылка № ${broadcastId}`}
            width={720}
            open={true}
            onCancel={handleCloseModal}
            destroyOnClose
            footer={null}
        >
            {isLoading ? (
                <Skeleton />
            ) : (
                <div style={{ marginTop: '32px' }}>
                    {contentBlocks.map((block, index) => (
                        <React.Fragment key={index}>
                            {renderContent(block)}
                        </React.Fragment>
                    ))}
                </div>
            )}

            <MassBroadcastsModalButtons
                status={data?.status}
                handleClose={handleCloseModal}
                handleCansel={handleCansel}
                handleEdit={() => handleEdit(broadcastId)}
            />
        </GenericModal>
    );
};
